import React, {
  useEffect, useState, useMemo, useCallback,
} from "react";
import { Modal, Spin } from "antd";
import moment from "moment";
import { toast } from "react-toastify";
import { getDelayedReportLogs } from "../../../actions/reportDelay";
import useAbortController from "../../Common/hooks/useAbortController";
import ReportDelayTable from "./ReportDelayTable";
import "./ReportDelayHistory.css";

export default function ReportDelayHistory({
  open,
  onCancel,
  record,
}) {
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const { setNewAbortController } = useAbortController();

  const fetchActivityLog = useCallback(async () => {
    if (!record) return;

    setLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, results, message, canceled,
    } = await getDelayedReportLogs(record.tests_in_sample_pk, newAbortController.signal);

    if (success && results) {
      setActivityData(results);
    } else if (!canceled) {
      toast.error(message || "Failed to fetch delay history");
      setActivityData([]);
    }

    setLoading(false);
  }, [record, setNewAbortController]);

  const columns = useMemo(() => ([
    {
      title: "Reported By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Reported At",
      dataIndex: "created_at",
      key: "created_at",
      onCell: () => ({ style: { minWidth: 120 } }),
      render: (value) => {
        if (!value) return "-";
        return value.split(" ")[0];
      },
    },
    {
      title: "Expected ETA",
      dataIndex: "expected_eta",
      key: "expected_eta",
      onCell: () => ({ style: { minWidth: 120 } }),
      render: (value) => (value ? moment(value).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      onCell: () => ({ style: { minWidth: 140 } }),
      render: (value) => value || "-",
    },
    {
      title: "Comment",
      dataIndex: "comments",
      key: "comments",
      ellipsis: true,
      onCell: () => ({ style: { minWidth: 200 } }),
      render: (value) => value || "-",
    },
  ]), []);

  useEffect(() => {
    if (open && record) {
      fetchActivityLog();
    } else {
      setActivityData([]);
    }
  }, [open, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const subheadingParts = useMemo(() => {
    if (!record) return [];
    const parts = [];
    if (record.workorder_id != null && String(record.workorder_id).length) {
      parts.push({ key: "wo", text: `WO ID: ${record.workorder_id}` });
    }
    if (record.genista_id != null && String(record.genista_id).length) {
      parts.push({ key: "genista", text: `Genista ID: ${record.genista_id}` });
    }
    if (record.test_name) {
      parts.push({ key: "test", text: `Test: ${record.test_name}` });
    }
    if (record.category_name) {
      parts.push({ key: "test_category", text: `Test Category: ${record.category_name}` });
    }
    if (record.current_eta) {
      const currentETA = moment(record.current_eta).format("MM/DD/YYYY");
      parts.push({ key: "current_eta", text: `Result Due By: ${currentETA}` });
    }
    return parts;
  }, [record]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Delay History"
      width={1000}
      footer={null}
      centered
      destroyOnClose
      className="ReportDelayHistory"
      styles={{ body: { maxHeight: "70vh", overflow: "hidden" } }}
    >
      <Spin spinning={loading} size="large">
        {record && subheadingParts.length > 0 && (
          <div className="ReportDelayHistory__Subheading">
            {subheadingParts.map((part, index) => (
              <React.Fragment key={part.key}>
                {index > 0 && (
                  <span className="ReportDelayHistory__SubheadingSeparator">|</span>
                )}
                <span>{part.text}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        <ReportDelayTable
          rowKey="id"
          dataSource={activityData || []}
          pagination
          columns={columns}
          // loading={loading}
          scroll={{ x: "max-content", y: "300px" }}
          onRow={() => ({ style: { cursor: "default" } })}
        />
      </Spin>
    </Modal>
  );
}
